export type QueueProcessResult<TResult> =
  | { success: true; result: TResult }
  | { success: false; error: string };

export type QueueJobArgs<TJob> = {
  job: TJob;
};

export type QueueSuccessArgs<TJob, TResult> = QueueJobArgs<TJob> & {
  result: TResult;
};

export type QueueErrorArgs<TJob> = QueueJobArgs<TJob> & {
  error: string;
};

export type QueueMergeOnDuplicateArgs<TJob> = {
  existingJob: TJob;
  incomingJob: TJob;
};

export enum QueueDiagnosticEventType {
  JobEnqueued = "job_enqueued",
  DuplicateJobMerged = "duplicate_job_merged",
  DuplicateJobDropped = "duplicate_job_dropped",
  JobStarted = "job_started",
  JobSucceeded = "job_succeeded",
  JobFailed = "job_failed",
  QueueReset = "queue_reset",
  StaleRunStopped = "stale_run_stopped",
}

export type QueueDiagnosticEvent<TJob> =
  | {
      type: QueueDiagnosticEventType.JobEnqueued;
      job: TJob;
      jobKey: string;
      queueSize: number;
    }
  | {
      type: QueueDiagnosticEventType.DuplicateJobMerged;
      existingJob: TJob;
      incomingJob: TJob;
      jobKey: string;
      queueSize: number;
    }
  | {
      type: QueueDiagnosticEventType.DuplicateJobDropped;
      incomingJob: TJob;
      jobKey: string;
      queueSize: number;
    }
  | {
      type: QueueDiagnosticEventType.JobStarted;
      job: TJob;
      jobKey: string;
      queueSize: number;
      runGeneration: number;
    }
  | {
      type: QueueDiagnosticEventType.JobSucceeded;
      job: TJob;
      jobKey: string;
      queueSize: number;
      runGeneration: number;
    }
  | {
      type: QueueDiagnosticEventType.JobFailed;
      job: TJob;
      jobKey: string;
      queueSize: number;
      runGeneration: number;
      errorMessage: string;
    }
  | {
      type: QueueDiagnosticEventType.QueueReset;
      clearedJobCount: number;
      nextRunGeneration: number;
    }
  | {
      type: QueueDiagnosticEventType.StaleRunStopped;
      runGeneration: number;
      currentGeneration: number;
    };

export interface QueueDiagnostics<TJob> {
  onEvent?: (args: { event: QueueDiagnosticEvent<TJob> }) => void;
}

export interface QueueCallbacks<TJob, TResult> {
  onSuccess?: (args: QueueSuccessArgs<TJob, TResult>) => void;
  onError?: (args: QueueErrorArgs<TJob>) => void;
}

export interface QueueOptions<TJob, TResult> {
  process: (args: QueueJobArgs<TJob>) => Promise<QueueProcessResult<TResult>>;
  callbacks?: QueueCallbacks<TJob, TResult>;
  diagnostics?: QueueDiagnostics<TJob>;
  dedupeKey: (args: QueueJobArgs<TJob>) => string;
  mergeOnDuplicate?: (args: QueueMergeOnDuplicateArgs<TJob>) => void;
}
